import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, RefreshCw, ExternalLink, PackageCheck } from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { DateInputBR } from '@components/DateInputBR';
import { isTrackingLink } from '../../utils/tracking';
import {
  shippingReportService,
  DeliveredOrderItem,
} from '../../services/shippingReportService';

// ─── Helpers ─────────────────────────────────────────────────────

const formatCurrency = (value?: number | null) =>
  value !== undefined && value !== null
    ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
    : '—';

const formatDateBR = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR');
};

// A data de entrega é o dado central do relatório, então mostra também a hora.
const formatDateTimeBR = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const toYMD = (d: Date) => d.toISOString().slice(0, 10);

const defaultFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return toYMD(d);
};

const PAGE_SIZE = 50;

export const DeliveredOrdersReportView: React.FC = () => {
  const [items, setItems] = useState<DeliveredOrderItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState(defaultFrom);
  const [dateTo, setDateTo] = useState(() => toYMD(new Date()));
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await shippingReportService.getDeliveredOrders({
        date_from: dateFrom,
        date_to: dateTo,
        page,
        pageSize: PAGE_SIZE,
      });
      setItems(res.data ?? []);
      setTotalItems(res.pagination?.total_items ?? 0);
      setTotalPages(res.pagination?.total_pages ?? 0);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar relatório');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, page]);

  useEffect(() => {
    load();
  }, [load]);

  // Trocar o período reinicia a paginação: a página 3 do período anterior não significa nada no novo.
  const onPeriodChange = (setter: (v: string) => void) => (value: string) => {
    setter(value);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Pedidos Entregues</h1>
          <p className="text-sm text-slate-500 mt-1">
            Pedidos com entrega confirmada, do mais recente para o mais antigo, com a transportadora
            utilizada e o link de rastreamento.
          </p>
        </div>
        <Button variant="outline" icon={<RefreshCw size={16} />} onClick={load} loading={loading}>
          Atualizar
        </Button>
      </div>

      {/* Filtro de período — sobre a data de ENTREGA */}
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
            Entregue de
          </label>
          <DateInputBR
            value={dateFrom}
            onChange={onPeriodChange(setDateFrom)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
            até
          </label>
          <DateInputBR
            value={dateTo}
            onChange={onPeriodChange(setDateTo)}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-600 ml-auto">
          <PackageCheck size={16} className="text-emerald-600" />
          <span>
            <strong>{totalItems}</strong> pedido(s) entregue(s)
          </span>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}

      <Card padding="none">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 flex items-center justify-center text-slate-400">
              <Loader2 className="animate-spin mr-2" size={18} /> Carregando…
            </div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              Nenhum pedido entregue no período selecionado.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Pedido</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Nº E-commerce</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Cliente</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Cidade/UF</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">NF</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Transportadora</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Data Envio</th>
                    <th className="text-left px-3 py-2 font-medium text-slate-600">Data Entrega</th>
                    <th className="text-right px-3 py-2 font-medium text-slate-600">Total</th>
                    <th className="text-center px-3 py-2 font-medium text-slate-600">Rastreio</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.order_id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-3 py-2 font-medium text-slate-800">{it.order_id}</td>
                      <td className="px-3 py-2 text-slate-600">{it.order_num_ecommerce || '—'}</td>
                      <td className="px-3 py-2 text-slate-700">{it.customer_name || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {it.ship_addr_city
                          ? `${it.ship_addr_city}${it.ship_addr_state ? '/' + it.ship_addr_state : ''}`
                          : '—'}
                      </td>
                      <td className="px-3 py-2 text-slate-600">{it.invoice_number || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{it.shipping_modality || '—'}</td>
                      <td className="px-3 py-2 text-slate-600">{formatDateBR(it.shipping_timestamp)}</td>
                      <td className="px-3 py-2 font-medium text-slate-800">
                        {formatDateTimeBR(it.delivery_timestamp)}
                      </td>
                      <td className="px-3 py-2 text-right text-slate-700">
                        {formatCurrency(it.order_sell_total)}
                      </td>
                      <td className="px-3 py-2 text-center">
                        {isTrackingLink(it.shipping_track_url) ? (
                          <a
                            href={it.shipping_track_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-emerald-600 hover:underline"
                          >
                            Rastrear <ExternalLink size={12} />
                          </a>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-slate-600">
          <span>
            Página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Anterior
            </Button>
            <Button
              variant="outline"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Próxima
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeliveredOrdersReportView;
